import React from 'react';
import { useLanguage } from '../LanguageContext';
import { useTheme } from '../ThemeContext';
import { useData } from '../DataContext';

const About = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { about, loading } = useData();

  if (loading || !about.data) {
    return (
      <section className="py-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </section>
    );
  }

  // Skills can be array of strings or array of objects {name, level, category}
  const skillsData = about.data.skills || [
    'Python', 'TensorFlow', 'PyTorch', 'Machine Learning', 'Deep Learning', 'NLP',
    'React', 'Next.js', 'Node.js', 'TypeScript', 'PostgreSQL', 'MongoDB',
    'Docker', 'AWS', 'GCP', 'FastAPI', 'OpenCV', 'Git'
  ];

  // Normalize to array of strings
  const skills = Array.isArray(skillsData)
    ? skillsData.map(skill => typeof skill === 'string' ? skill : skill.name)
    : [];

  return (
    <section id="about" className={`py-24 relative overflow-hidden ${isDark ? 'bg-[#0a0f1a]' : 'bg-white'}`}>
      {/* Background */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/3 left-1/4 w-[500px] h-[500px] ${isDark ? 'bg-blue-500/5' : 'bg-blue-200/20'} rounded-full blur-[150px]`}></div>

        {/* Grid Pattern */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: isDark
              ? `linear-gradient(rgba(59, 130, 246, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.08) 1px, transparent 1px)`
              : `linear-gradient(rgba(148, 163, 184, 0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 0.15) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        ></div>

        {/* Horizontal Sparkles */}
        <div className={`sparkle-h1 absolute w-16 h-[2px] bg-gradient-to-r from-transparent ${isDark ? 'via-blue-400' : 'via-blue-400'} to-transparent`} style={{ top: '20%' }}></div>
        <div className={`sparkle-h2 absolute w-24 h-[2px] bg-gradient-to-r from-transparent ${isDark ? 'via-cyan-400' : 'via-cyan-400'} to-transparent`} style={{ top: '40%' }}></div>
        <div className={`sparkle-h3 absolute w-20 h-[2px] bg-gradient-to-r from-transparent ${isDark ? 'via-blue-500' : 'via-blue-500'} to-transparent`} style={{ top: '60%' }}></div>
        <div className={`sparkle-h4 absolute w-16 h-[2px] bg-gradient-to-r from-transparent ${isDark ? 'via-indigo-400' : 'via-indigo-400'} to-transparent`} style={{ top: '80%' }}></div>

        {/* Vertical Sparkles */}
        <div className={`sparkle-v1 absolute w-[2px] h-16 bg-gradient-to-b from-transparent ${isDark ? 'via-blue-400' : 'via-blue-400'} to-transparent`} style={{ left: '15%' }}></div>
        <div className={`sparkle-v2 absolute w-[2px] h-20 bg-gradient-to-b from-transparent ${isDark ? 'via-cyan-400' : 'via-cyan-400'} to-transparent`} style={{ left: '40%' }}></div>
        <div className={`sparkle-v3 absolute w-[2px] h-24 bg-gradient-to-b from-transparent ${isDark ? 'via-blue-500' : 'via-blue-500'} to-transparent`} style={{ left: '65%' }}></div>
        <div className={`sparkle-v4 absolute w-[2px] h-16 bg-gradient-to-b from-transparent ${isDark ? 'via-indigo-400' : 'via-indigo-400'} to-transparent`} style={{ left: '85%' }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10 max-w-7xl">

        {/* Header */}
        <div className="text-center mb-16">
          <p className={`font-light tracking-widest uppercase mb-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{t.about.sectionTitle}</p>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-black mb-6 ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {about.getLocalized('title', language) || `${t.about.title1} ${t.about.title2} ${t.about.title3}`}
          </h2>
        </div>

        {/* Main Content */}
        <div className="max-w-5xl mx-auto">
          {/* Bio Text */}
          <div className={`space-y-6 text-lg md:text-xl leading-relaxed mb-16 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
            <p>{about.getLocalized('description', language) || t.about.bio1}</p>
            <p>{t.about.bio2}</p>
            <p>{t.about.bio3}</p>
          </div>

          {/* Skills */}
          <div>
            <h4 className={`text-sm font-semibold uppercase tracking-widest mb-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{t.about.technologies}</h4>
            <div className="flex flex-wrap gap-3">
              {skills.map((skill, index) => (
                <span
                  key={index}
                  className={`px-5 py-2.5 text-base border rounded-full transition-all duration-200 ${
                    isDark
                      ? 'text-gray-400 border-white/10 hover:text-white hover:border-blue-500/30'
                      : 'text-slate-600 border-slate-200 bg-slate-50/50 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50'
                  }`}
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
